/**
 * =============================================================================
 * PATIENT JOURNEY - CONFIRMATION DIALOG
 * =============================================================================
 *
 * ONE dialog for every journey confirmation. Only the tone, the copy and the
 * button handlers change:
 *
 *   leaving the workflow midway   warning  Go Back / Continue Editing
 *   closing the case midway       warning  Go Back / Close Anyway
 *   the case has been closed      success  Got It
 *
 * The copy and wiring live in `useJourneyConfirmations`, so both the case
 * details page and the Close Case popup show exactly the same dialogs.
 * =============================================================================
 */

import React from "react";
import { Box, Dialog, Typography, Button, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import { FONT, JOURNEY_COLORS } from "./journeyStyles";

export const CONFIRM_TONE = {
    WARNING: "warning",
    SUCCESS: "success",
};

/* -----------------------------------------------------------------------------
   BUTTONS
----------------------------------------------------------------------------- */

const buttonSx = (isPrimary) => ({
    flex: 1,
    width: "100%",
    height: { xs: "46px", sm: "48px" },
    borderRadius: "10px",
    textTransform: "none",
    fontFamily: FONT,
    fontSize: "15px",
    fontWeight: 600,
    boxShadow: "none",
    backgroundColor: isPrimary
        ? JOURNEY_COLORS.accent
        : JOURNEY_COLORS.accentSoft,
    color: isPrimary ? "#FFFFFF" : JOURNEY_COLORS.accent,
    "&:hover": {
        boxShadow: "none",
        backgroundColor: isPrimary
            ? JOURNEY_COLORS.accent
            : JOURNEY_COLORS.accentSoft,
        filter: "brightness(0.96)",
    },
});

/* -----------------------------------------------------------------------------
   COMPONENT
----------------------------------------------------------------------------- */

const JourneyConfirmDialog = ({
    open = false,
    tone = CONFIRM_TONE.WARNING,
    title = "",
    message = "",
    primaryLabel = "",
    secondaryLabel = "",
    onPrimary,
    onSecondary,
    onClose,
}) => {
    const isSuccess = tone === CONFIRM_TONE.SUCCESS;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: "100%",
                    maxWidth: "460px",
                    margin: { xs: "12px", sm: "16px" },
                    borderRadius: "20px",
                    padding: { xs: "22px 18px 22px", sm: "26px 30px 30px" },
                    boxSizing: "border-box",
                    position: "relative",
                    backgroundColor: JOURNEY_COLORS.surface,
                    boxShadow: "var(--app-shadow)",
                },
            }}
        >
            <IconButton
                onClick={onClose}
                size="small"
                aria-label="Close"
                sx={{
                    position: "absolute",
                    top: "14px",
                    right: "14px",
                    color: JOURNEY_COLORS.text,
                }}
            >
                <CloseIcon sx={{ fontSize: "24px" }} />
            </IconButton>

            {/* ============ ICON ============ */}

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    marginBottom: "18px",
                    marginTop: "10px",
                }}
            >
                {isSuccess ? (
                    <CheckCircleIcon
                        sx={{
                            fontSize: { xs: "54px", sm: "62px" },
                            color: JOURNEY_COLORS.success,
                        }}
                    />
                ) : (
                    <WarningRoundedIcon
                        sx={{
                            fontSize: { xs: "58px", sm: "66px" },
                            color: JOURNEY_COLORS.danger,
                        }}
                    />
                )}
            </Box>

            {/* ============ COPY ============ */}

            <Typography
                sx={{
                    fontFamily: FONT,
                    fontSize: { xs: "18px", sm: "20px" },
                    lineHeight: { xs: "25px", sm: "28px" },
                    fontWeight: 700,
                    letterSpacing: "-0.01em",
                    color: JOURNEY_COLORS.text,
                    textAlign: "center",
                }}
            >
                {title}
            </Typography>

            {message && (
                <Typography
                    sx={{
                        fontFamily: FONT,
                        fontSize: { xs: "14px", sm: "15px" },
                        lineHeight: { xs: "21px", sm: "24px" },
                        fontWeight: 400,
                        color: JOURNEY_COLORS.textMuted,
                        textAlign: "center",
                        marginTop: "10px",
                    }}
                >
                    {message}
                </Typography>
            )}

            {/* ============ ACTIONS ============ */}

            <Box
                sx={{
                    display: "flex",
                    /* column-reverse keeps the primary action nearest the thumb. */
                    flexDirection: { xs: "column-reverse", sm: "row" },
                    gap: { xs: "10px", sm: "14px" },
                    marginTop: { xs: "20px", sm: "26px" },
                }}
            >
                {secondaryLabel && (
                    <Button
                        disableElevation
                        onClick={onSecondary}
                        sx={buttonSx(false)}
                    >
                        {secondaryLabel}
                    </Button>
                )}

                {primaryLabel && (
                    <Button
                        disableElevation
                        variant="contained"
                        onClick={onPrimary}
                        sx={buttonSx(true)}
                    >
                        {primaryLabel}
                    </Button>
                )}
            </Box>
        </Dialog>
    );
};

export default JourneyConfirmDialog;
